import { useParams } from 'react-router-dom';
import { Tabs } from '@medtronic/pecc-react-component-library-js';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useGetProjectQuery } from '~/api/v2/hooks';
import { BreadcrumbsNavigation } from '~/components';
import { usePersistedTab } from '~/hooks';
import { HomeIcon } from '~/icons';
import { useAppSelector } from '~/redux';
import { selectCurrentUserApplicationRole, selectCurrentUserId } from '~/redux/slices/currentUserSlice';
import { PageTabsKeyEnum } from '~/redux/slices/pageTabsSlice';
import { ProjectTabsEnum, getPriviligies, useProjectTabEnabled } from '~/utils';
import { loadingContainerStyles } from './components/styles';
import { ETLJobsTab, ProjectDetailsTab, ProjectMembersTab, StudioIntegrationTab, WorkbenchTab } from './tabs';

const ProjectDetailsPage = () => {
  const projectId = +useParams().projectid!;
  const userId = useAppSelector(selectCurrentUserId);
  const userApplicationRole = useAppSelector(selectCurrentUserApplicationRole);
  const { data, isLoading } = useGetProjectQuery({ projectId });
  const userProjectRole = data?.members?.find(({ id }) => id === userId)?.role_id;
  const { userApplicationPriviligies, userProjectPriviligies } = {
    userApplicationPriviligies: getPriviligies(userApplicationRole),
    userProjectPriviligies: getPriviligies(userProjectRole),
  };

  const isTabEnabled = useProjectTabEnabled({
    projectType: data?.type,
    userApplicationPriviligies,
    userProjectPriviligies,
  });

  const projectTabs = [
    {
      label: ProjectTabsEnum.PROJECT_DETAILS_TAB,
      content: <ProjectDetailsTab />,
      visible: isTabEnabled(ProjectTabsEnum.PROJECT_DETAILS_TAB),
    },
    {
      label: ProjectTabsEnum.STUDIO_INTEGRATION_TAB,
      content: <StudioIntegrationTab />,
      visible: isTabEnabled(ProjectTabsEnum.STUDIO_INTEGRATION_TAB),
    },
    {
      label: ProjectTabsEnum.ETL_JOBS_TAB,
      content: <ETLJobsTab />,
      visible: isTabEnabled(ProjectTabsEnum.ETL_JOBS_TAB),
    },
    {
      label: ProjectTabsEnum.WORKBENCH_TAB,
      content: <WorkbenchTab />,
      visible: isTabEnabled(ProjectTabsEnum.WORKBENCH_TAB),
    },
    {
      label: ProjectTabsEnum.PROJECT_MEMBERS_TAB,
      content: <ProjectMembersTab />,
      visible: isTabEnabled(ProjectTabsEnum.PROJECT_MEMBERS_TAB),
    },
  ];

  const visibleTabs = projectTabs.filter((tab) => tab.visible);
  const { selectedTabIndex, handleTabChange } = usePersistedTab(PageTabsKeyEnum.PROJECT_DETAILS, visibleTabs);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {isLoading ? (
        <Box sx={loadingContainerStyles}>
          <CircularProgress size={70} sx={{ color: '#1010eb' }} />
        </Box>
      ) : (
        <>
          <Typography variant="h3" whiteSpace="pre-wrap" sx={{ wordBreak: 'break-word' }}>
            Projects - <strong>{data?.name}</strong>
          </Typography>
          <BreadcrumbsNavigation
            navigationConfig={[
              { name: <HomeIcon />, path: '' },
              {
                name: 'Projects',
                path: '..',
              },
              {
                name: data?.name,
                path: '.',
              },
            ]}
          />
          <Paper elevation={2} sx={{ mt: '1rem', width: '100%' }}>
            <Tabs
              labelSx={{ textTransform: 'none' }}
              key={visibleTabs.map((tab) => tab.label).join('|')}
              isLeftAlign
              tabsData={visibleTabs}
              redirectTabIndex={selectedTabIndex}
              setRedirectTabIndexOnChange={handleTabChange}
              width="100%"
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ProjectDetailsPage;
